/* resend_required.js — live admin list of ballot resends that have been asked
   for and not yet marked complete.

   Subscribes to ResendRequiredChannel, which only says THAT something changed.
   The list is then re-read from the server and swapped in, so the markup lives
   in one place (views/dashboards/resend_required.html.erb) and a burst of
   changes — a bulk resend, "Mark All as Complete" — costs one re-read. */
document.addEventListener('turbolinks:load', () => {
  const panel = document.getElementById('resend-required');
  if (!panel) return;
  if (panel.dataset.subscribed === 'true') return;

  const list         = document.getElementById('rr-list');
  const empty        = document.getElementById('rr-empty');
  const countEl      = document.getElementById('rr-count');
  const orderCountEl = document.getElementById('rr-order-count');
  const dotEl        = document.getElementById('rr-live-dot');
  const labelEl      = document.getElementById('rr-live-label');
  const badgeEl      = document.getElementById('rr-live-badge');
  if (!list) return;

  function setLive(isLive) {
    if (!dotEl || !labelEl || !badgeEl) return;

    dotEl.className = isLive ? 'w-1.5 h-1.5 rounded-full bg-emerald-500' : 'w-1.5 h-1.5 rounded-full bg-gray-400';
    labelEl.textContent = isLive ? 'Live' : 'Offline';
    badgeEl.className = isLive
      ? 'inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200'
      : 'inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-gray-100 text-gray-500 border border-gray-200';
  }

  /* Prefer the count the server sent — it is the authoritative one. Fall back
     to the rows on the page. */
  function refreshCounts(serverCount) {
    const orders = list.querySelectorAll('.rr-order').length;

    if (countEl) {
      countEl.textContent = (typeof serverCount === 'number')
        ? serverCount
        : list.querySelectorAll('.rr-row').length;
    }
    if (orderCountEl) orderCountEl.textContent = orders;
    if (empty) empty.classList.toggle('hidden', orders > 0);
  }

  if (!window.App || !window.App.cable) {
    // No consumer on the page (asset build issue, or cable disabled) — the list
    // the server rendered is still correct, so say so rather than lying "Live".
    setLive(false);
    return;
  }

  /* Re-reads the list. Also run on every (re)connection, before calling the
     page live: signals sent while the socket was down are gone for good, and a
     resend completed during an outage would otherwise sit here indefinitely
     under a "Live" badge. */
  function fetchList() {
    return fetch(window.location.href, { credentials: 'same-origin', headers: { Accept: 'text/html' } })
      .then((response) => {
        if (!response.ok) throw new Error('HTTP ' + response.status);
        return response.text();
      })
      .then((html) => {
        const fresh = new DOMParser().parseFromString(html, 'text/html');
        if (!fresh.getElementById('rr-list')) throw new Error('list missing');
        return fresh;
      });
  }

  /* `connection` counts connections and disconnections, so a re-read that began
     under an earlier one can tell it is stale and must touch nothing — neither
     the list nor the badge.

     A signal that arrives while a re-read is in flight sets `again`: that page
     may have been rendered before the change, so it is read once more rather
     than trusted. */
  let connection = 0;
  let reading = false;
  let again = false;
  let timer = null;

  function refresh() {
    if (reading) {
      again = true;
      return;
    }

    const mine = connection;
    reading = true;
    again = false;

    fetchList()
      .then((fresh) => {
        if (mine !== connection) return;

        const freshCount = fresh.getElementById('rr-count');
        list.innerHTML = fresh.getElementById('rr-list').innerHTML;
        refreshCounts(freshCount ? parseInt(freshCount.textContent, 10) : undefined);
        setLive(true);
      })
      .catch(() => {
        // The list could not be re-read, so it may be missing changes.
        if (mine === connection) setLive(false);
      })
      .then(() => {
        reading = false;
        if (again && mine === connection) refresh();
      });
  }

  // A bulk action sends one signal per row; wait for the burst to settle.
  function refreshSoon() {
    clearTimeout(timer);
    timer = setTimeout(refresh, 300);
  }

  function stop() {
    connection += 1;
    again = false;
    clearTimeout(timer);
    setLive(false);
  }

  const subscription = window.App.cable.subscriptions.create('ResendRequiredChannel', {
    connected() {
      connection += 1;
      refresh();
    },

    disconnected() {
      stop();
    },

    rejected() {
      stop();
    },

    received(data) {
      if (!data || data.type !== 'resend_required_changed') return;

      refreshSoon();
    }
  });

  panel.dataset.subscribed = 'true';

  /* Leaving the page: drop the subscription, which otherwise lives on holding
     this page's elements and stacks up a second one on the next visit. */
  document.addEventListener('turbolinks:before-render', () => {
    stop();
    subscription.unsubscribe();
    panel.dataset.subscribed = 'false';
  }, { once: true });
});
